import { useState } from 'react';
import SEOHead from '../../../components/SEOHead';

export default function KindergartenPage() {
  const [activeTab, setActiveTab] = useState<'lessons' | 'worksheets'>('lessons');
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';
  const jsonLd = { '@context': 'https://schema.org', '@type': 'Course', name: 'Kindergarten Math | Counting, Shapes & Patterns | Augustine Anyimadu-Ahenkae', description: 'Kindergarten mathematics curriculum covering counting 1-10, basic shapes, patterns, comparing sizes, and number recognition. Aligned with Common Core Kindergarten standards.', url: `${siteUrl}/grades/kindergarten`, provider: { '@type': 'Person', name: 'Augustine Anyimadu-Ahenkae', url: siteUrl }, educationalLevel: 'Kindergarten', inLanguage: 'en' };

  const lessons = [
    {
      id: 1,
      title: 'Counting 1-10',
      description: 'Learn to count from 1 to 10 with fun activities',
      duration: '15 min',
      difficulty: 'Beginner',
      icon: 'ri-number-1',
    },
    {
      id: 2,
      title: 'Basic Shapes',
      description: 'Identify circles, squares, triangles, and rectangles',
      duration: '20 min',
      difficulty: 'Beginner',
      icon: 'ri-shape-line',
    },
    {
      id: 3,
      title: 'Comparing Sizes',
      description: 'Learn big vs small, tall vs short',
      duration: '15 min',
      difficulty: 'Beginner',
      icon: 'ri-arrow-left-right-line',
    },
    {
      id: 4,
      title: 'Simple Patterns',
      description: 'Recognize and create simple patterns',
      duration: '20 min',
      difficulty: 'Beginner',
      icon: 'ri-grid-line',
    },
    {
      id: 5,
      title: 'Number Recognition',
      description: 'Match numbers with quantities',
      duration: '15 min',
      difficulty: 'Beginner',
      icon: 'ri-eye-line',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      <SEOHead title="Kindergarten Math New York | Counting Shapes Patterns | Augustine Anyimadu-Ahenkae" description="Fun Kindergarten math lessons covering counting 1-10, basic shapes, patterns, and number recognition. Aligned with Common Core K standards. Interactive lessons and printable worksheets." canonical="/grades/kindergarten" keywords="Kindergarten math, counting numbers, basic shapes, kindergarten Common Core, K math lessons" jsonLd={jsonLd} />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl flex items-center justify-center">
              <i className="ri-bear-smile-line text-3xl text-white"></i>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Kindergarten Math</h1>
              <p className="text-gray-600 mt-1">Building foundational math skills through play</p>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-6">
            <button
              onClick={() => setActiveTab('lessons')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'lessons'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Lessons
            </button>
            <button
              onClick={() => setActiveTab('worksheets')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'worksheets'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Worksheets
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === 'lessons' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lessons.map((lesson) => (
              <div
                key={lesson.id}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${lesson.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{lesson.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{lesson.description}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-purple-600 font-medium">{lesson.duration}</span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    {lesson.difficulty}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'worksheets' && (
          <div className="space-y-8">
            {/* Worksheet Preview Banner */}
            <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl p-8 text-white">
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-file-text-line text-2xl"></i>
                    <h2 className="text-2xl font-bold">Practice Worksheets Available</h2>
                  </div>
                  <p className="text-pink-100 mb-6">
                    Download our free sample worksheet and explore more practice materials for Kindergarten
                  </p>
                  
                  {/* Free Sample Card */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 mb-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-green-400 text-green-900 rounded-md text-xs font-bold">
                            FREE SAMPLE
                          </span>
                        </div>
                        <h3 className="text-lg font-bold mb-2">Counting & Number Recognition</h3>
                        <p className="text-pink-100 text-sm mb-3">
                          Fun activities for counting objects and recognizing numbers 1-10
                        </p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1">
                            <i className="ri-file-line"></i>
                            6 pages
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-checkbox-circle-line"></i>
                            20 problems
                          </span>
                        </div>
                      </div>
                      <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                        <i className="ri-download-cloud-line text-3xl"></i>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => alert('Download will start shortly!')}
                      className="px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-pink-50 transition-all flex items-center gap-2 whitespace-nowrap"
                    >
                      <i className="ri-download-line"></i>
                      Download Free Sample
                    </button>
                    <a
                      href="/worksheets?category=kindergarten"
                      className="px-6 py-3 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-all flex items-center gap-2 whitespace-nowrap"
                    >
                      View All Worksheets
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Membership Upsell */}
            <div className="bg-white rounded-xl p-8 border-2 border-purple-200">
              <div className="text-center max-w-2xl mx-auto">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-vip-crown-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Unlock 50+ Kindergarten Worksheets
                </h3>
                <p className="text-gray-600 mb-6">
                  Get access to our complete collection of printable worksheets covering counting, shapes, patterns, and more with step-by-step guidance
                </p>
                <a
                  href="/#membership"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg font-semibold hover:from-pink-600 hover:to-purple-700 transition-all whitespace-nowrap"
                >
                  Choose Your Plan
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}